<template>
  <div>
    <add-or-update-country-form
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData"
      @resetUpdateData="resetUpdateData"
    />
    <h1 class="text-center my-2">Countries</h1>
    <location-code-guide level="country" />

    <b-row class="mb-1">
      <b-col md="4">
        <b-form-group label="RMC" label-for="rmc-filter">
          <v-select
            id="rmc-filter"
            v-model="rmcFilter"
            :loading="rmcLoading"
            :options="rmcOptions"
            :reduce="(val) => val.value"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            placeholder="All RMCs"
          />
        </b-form-group>
      </b-col>
      <b-col md="4">
        <b-form-group label="Status" label-for="status-filter">
          <v-select
            id="status-filter"
            v-model="statusFilter"
            :clearable="false"
            :options="locationStatusOptions"
            :reduce="(val) => val.value"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          />
        </b-form-group>
      </b-col>
      <b-col md="4" class="d-flex align-items-center">
        <b-form-checkbox v-model="supportedOnly" class="mt-2">
          Mission countries only
        </b-form-checkbox>
      </b-col>
    </b-row>

    <list-table
      :records="records"
      :columns="tableColumns"
      :total-records="totalRecords"
      :search-filter.sync="search"
      :current-page-number.sync="currentPage"
      :show-search="true"
      :show-filter-by-default="true"
      :show-export-button="can('countries:CanExport')"
      selection-mode="multi"
      @selected-rows="onRowsSelected"
      @export="onExport"
    >
      <template v-if="can('countries:CanAddNew') || can('countries:CanUpdate')" #action_button>
        <b-button
          v-if="can('countries:CanUpdate') && selectedCountries.length > 0"
          v-ripple.400="'rgba(40, 199, 111, 0.15)'"
          variant="success"
          class="mr-1"
          @click="bulkActivate"
        >
          <feather-icon icon="CheckCircleIcon" class="mr-50" />
          <span class="align-middle">Mark {{ selectedCountries.length }} Countr{{ selectedCountries.length > 1 ? 'ies' : 'y' }} as Mission</span>
        </b-button>

        <b-button
          v-if="can('countries:CanAddNew')"
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          @click="isAddOrUpdateActive = true"
        >
          <span class="align-middle text-lg">Add Country</span>
        </b-button>
      </template>

      <template #cell(supported)="data">
        <b-badge pill :variant="data.item.supported ? 'light-success' : 'light-secondary'">
          {{ data.item.supported ? 'YES' : 'NO' }}
        </b-badge>
      </template>

      <template #cell(is_active)="data">
        <b-badge pill :variant="isLocationActive(data.item) ? 'light-success' : 'light-secondary'">
          {{ isLocationActive(data.item) ? 'Active' : 'Inactive' }}
        </b-badge>
      </template>

      <template #cell(rmc)="data">
        {{ data.item.rmc ? data.item.rmc.title : '-' }}
      </template>

      <template #cell(block)="data">
        {{ data.item.block ? data.item.block.title : '-' }}
      </template>

      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item v-if="can('countries:CanUpdate')" @click="onEdit(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit</span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('countries:CanUpdate')" @click="onToggleStatus(data.item)">
            <feather-icon :icon="isLocationActive(data.item) ? 'EyeOffIcon' : 'EyeIcon'" />
            <span class="align-middle ml-50">{{ isLocationActive(data.item) ? 'Deactivate' : 'Activate' }}</span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('countries:CanDelete')" @click="onDelete(data.item._id)">
            <feather-icon icon="TrashIcon" />
            <span class="align-middle ml-50">Delete</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import FileDownload from "js-file-download";
import Ripple from 'vue-ripple-directive'
import ListTable from "@core/components/ListTable/ListTable.vue";
import AddOrUpdateCountryForm from "@/@core/components/shared/geo/AddOrUpdateCountryForm.vue"
import LocationCodeGuide from "@/@core/components/shared/geo/LocationCodeGuide.vue"
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import LocationStatusOptions from "@core/mixins/LocationStatusOptions";

import { debounce, get } from "lodash";
import { BRow, BCol, BButton, BBadge, BFormGroup, BFormCheckbox, BDropdown, BDropdownItem } from "bootstrap-vue";

const watchHandler = {
  handler(){
    this.debounceFetchAllData()
  },
  immediate: false
}

const watchHandlerRequiresPageReset = {
  handler(){
    this.currentPage = 1;
    this.debounceFetchAllData()
  },
  immediate: false
}

export default {
  components: {
    LocationCodeGuide,
    BRow,
    BCol,
    BButton,
    BBadge,
    vSelect,
    ListTable,
    BFormGroup,
    BFormCheckbox,
    BDropdown,
    BDropdownItem,
    AddOrUpdateCountryForm,
  },
  directives: {
    Ripple,
  },
  mixins: [LocationStatusOptions],
  data() {
    return {
      loading: false,
      records: [],

      search: "",
      rmcFilter: null,
      supportedOnly: false,
      rmcLoading: false,
      rmcOptions: [],
      selectedCountries: [],

      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: 'check', label: '', sortable: false },
        { key: 'country', label: "Country", sortable: false },
        { key: 'iso_code', label: "ISO Code", sortable: false },
        { key: 'supported', label: "Mission Country", sortable: false },
        { key: 'is_active', label: "Status", sortable: false },
        { key: 'rmc', label: "RMC", sortable: false },
        { key: 'block', label: "Mission Bloc", sortable: false },
        { key: 'actions' },
      ]
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    currentPage: watchHandler,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
    rmcFilter: watchHandlerRequiresPageReset,
    supportedOnly: watchHandlerRequiresPageReset,
    statusFilter: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);
    this.fetchRmcOptions();

    const {
      search,
      page = 1,
      limit = this.perPage,
      rmc_id,
      supported,
    } = this.$route.query;

    if (rmc_id) {
      this.rmcFilter = rmc_id;
    }
    if (supported) {
      this.supportedOnly = String(supported) === "true";
    }

    this.restoreLocationStatusFromQuery(this.$route.query);

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;

    this.fetchAllData();
  },
  methods: {
    async fetchRmcOptions() {
      try {
        this.rmcLoading = true;

        const request = await this.useJwt().sharedService.fetchRmc({ limit: 500 });
        const { data } = request.data;

        this.rmcOptions = (data || []).map((item) => ({
          label: item.title,
          value: item._id
        }))
      } catch (error) {
        // Silently ignore - the RMC filter is a convenience, not critical path.
      } finally {
        this.rmcLoading = false;
      }
    },
    async fetchAllData() {
      try {
        this.loading = true;

        const query = {
          limit: this.perPage,
          search: this.search,
          page: this.currentPage,
          ...this.buildFilterQuery(),
        }

        await this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().sharedService.fetchGeoCountries(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onDelete(id){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Delete?',
          text: "Do you want to delete this record?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().sharedService.deleteGeoCountry(id)

        // The deleted row may still be sitting in the multi-select buffer.
        this.selectedCountries = this.selectedCountries.filter((country) => country._id !== id);

        await this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Record deleted successfully'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onToggleStatus(country){
      return this.toggleLocationStatus(country, {
        serviceMethod: "updateGeoCountryStatus",
        label: "Country",
      });
    },
    buildFilterQuery() {
      const query = {};
      if (this.rmcFilter) query.rmc_id = this.rmcFilter;
      if (this.supportedOnly) query.supported = true;
      Object.assign(query, this.locationStatusQuery);
      return query;
    },
    async onExport() {
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current search and filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          search: this.search,
          ...this.buildFilterQuery(),
        }

        const response = await this.useJwt().sharedService.exportGeoCountries(query);
        FileDownload(response.data, "countries-export.xlsx");
      } catch (error) {
        const error_message = get(error, "response.statusText") || get(error, "response.data.message") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onEdit(update){
      this.updateData = update;
      this.isAddOrUpdateActive = true;
    },
    resetUpdateData(){
      if (this.updateData){
        this.updateData = null
      }
    },
    onRowsSelected(selectedRows){
      this.selectedCountries = selectedRows;
    },
    async bulkActivate(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Mission Countries?',
          text: `Do you want to mark ${this.selectedCountries.length} ${this.selectedCountries.length > 1 ? 'countries' : 'country'} as mission countries?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const ids = this.selectedCountries.map((country) => country._id);
        await this.useJwt().sharedService.bulkUpdateGeoCountriesSupported(ids, true);

        this.selectedCountries = [];
        await this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Countries marked as mission countries successfully'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
  }
}
</script>
